import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { IconButton } from '../../button/button';
import { forceAssert } from '../../common-functions';
import type { TypeOrArray } from '../../common-interfaces';
import OverlayPanel from '../../common/overlay-panel/overlay-panel.component';
import type { OverlayScrollDetectionType } from '../../common/overlay-panel/overlay-panel.props';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import { ContinuousScrollerComparisonType } from '../../continuous-scroller/continuous-scroller-types';
import { DomTargetPosition } from '../../enums';
import { isDropDownResult } from '../../fields/drop-down/drop-down-common';
import type {
  DropDownActionTemplateProps,
  DropDownNoSelection,
  DropDownResult,
} from '../../fields/drop-down/drop-down-types';
import DropDown from '../../fields/drop-down/drop-down.component';
import Input from '../../fields/input/input.component';
import type { JsonData } from '../../generic-types';
import { OdinIconSize } from '../../odin-icon/odin-icon.component';
import type { DataGridColumnSettings } from '../data-grid-settings.interface';
import ClearFilterButton from './common/clear-filter-button.component';

export const comparisonDropDownData: JsonData = [
  { value: ContinuousScrollerComparisonType.Equals, text: 'Equals' },
  { value: ContinuousScrollerComparisonType.GreaterThanEqualTo, text: 'Greater than or equal to' },
  { value: ContinuousScrollerComparisonType.LessThanEqualTo, text: 'Less than or equal to' },
  { value: ContinuousScrollerComparisonType.Between, text: 'Between' },
];

const defaultComparisonValue = {
  value: ContinuousScrollerComparisonType.Equals,
  text: 'Equals',
};

interface DataGridNumericFilterPanelProps {
  column: DataGridColumnSettings;
  currentValue: string | Array<string>;
  target: HTMLElement | null;
  visible: boolean;
  scrollDetection?: OverlayScrollDetectionType;
  onFilterChange: (filter: ContinuousScrollerFilter) => void;
  onClearFilters: () => void;
  onHidePanel: () => void;
}

function DataGridNumericFilterPanel({
  column,
  currentValue,
  target,
  visible,
  scrollDetection,
  onFilterChange,
  onClearFilters,
  onHidePanel,
}: DataGridNumericFilterPanelProps) {
  const [selectedComparisonValue, setSelectedComparisonValue] = useState<DropDownResult>(defaultComparisonValue);
  //Used as value when only one input is visible
  const [minimumValue, setMinimumValue] = useState('');
  const [maximumValue, setMaximumValue] = useState('');
  const [maxValueError, setMaxValueError] = useState<string | undefined>(undefined);

  const { t } = useTranslation();

  useEffect(() => {
    if (currentValue.length === 0) {
      setMinimumValue('');
      setMaximumValue('');
    } else {
      if (Array.isArray(currentValue) && currentValue.length === 2) {
        setMinimumValue(currentValue[0]);
        setMaximumValue(currentValue[1]);
      } else if (!Array.isArray(currentValue)) {
        if (
          selectedComparisonValue.value === ContinuousScrollerComparisonType.Between &&
          currentValue.split(',').length === 2
        ) {
          const values = currentValue.split(',');
          setMinimumValue(values[0]);
          setMaximumValue(values[1]);
        } else {
          setMinimumValue(currentValue);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: sync internal state from prop, selectedComparisonValue.value is what we're setting
  }, [currentValue]);

  useEffect(() => {
    if (column.searchComparison) {
      const comparison =
        comparisonDropDownData.find((comparison) => comparison.value === column.searchComparison) ??
        defaultComparisonValue;
      setSelectedComparisonValue(forceAssert<DropDownResult>(comparison));
    }
  }, [column.searchComparison]);

  const applyFilters = useCallback(
    (value: string, secondValue: string) => {
      if (
        selectedComparisonValue.value === ContinuousScrollerComparisonType.Between &&
        (value.length === 0 || secondValue.length === 0)
      ) {
        return;
      }

      // column.id is Key (string | number | symbol); ContinuousScrollerFilter only supports string | number.
      // If a symbol is provided, fall back to symbol.description (or full toString) to maintain identifier stability.
      let resolvedId: string | number;
      if (typeof column.id === 'symbol') {
        const sym: symbol = column.id;
        // Use description if available (Node 16+), else fall back to Symbol() string form.
        resolvedId = sym.description ?? String(sym);
      } else {
        resolvedId = column.id;
      }
      const filter: ContinuousScrollerFilter = {
        scrollerId: resolvedId,
        field: column.field,
        customDataProperties: column.customDataProperties,
        value: '',
      };

      if (value.length > 0) {
        switch (selectedComparisonValue.value) {
          case ContinuousScrollerComparisonType.Between:
            filter.comparison = ContinuousScrollerComparisonType.Between;
            filter.value = [value, secondValue];
            break;
          case ContinuousScrollerComparisonType.GreaterThanEqualTo:
            filter.comparison = ContinuousScrollerComparisonType.GreaterThanEqualTo;
            filter.value = value;
            break;
          case ContinuousScrollerComparisonType.LessThanEqualTo:
            filter.comparison = ContinuousScrollerComparisonType.LessThanEqualTo;
            filter.value = value;
            break;
          case ContinuousScrollerComparisonType.Equals:
            filter.comparison = ContinuousScrollerComparisonType.Equals;
            filter.value = value;
            break;
        }
      }

      onFilterChange(filter);
    },
    [column.id, column.field, column.customDataProperties, selectedComparisonValue.value, onFilterChange]
  );

  const resetFilters = () => {
    onClearFilters();
    setMaximumValue('');
    setMinimumValue('');
    setMaxValueError(undefined);
  };

  const onMinimumValueChange = (value: string | undefined) => {
    const newMinimumValue = value ?? '';
    setMinimumValue(newMinimumValue);
  };

  const onMaximumValueChange = (value: string | undefined) => {
    const newMaximumValue = value ?? '';
    setMaximumValue(newMaximumValue);
  };

  const onComparisonValueChange = (value: TypeOrArray<DropDownResult> | DropDownNoSelection | undefined) => {
    if (isDropDownResult(value)) {
      resetFilters();
      setSelectedComparisonValue(value);
    }
  };

  useEffect(() => {
    if (selectedComparisonValue.value === ContinuousScrollerComparisonType.Between) {
      if (minimumValue.length === 0 || maximumValue.length === 0) {
        setMaxValueError(undefined);
        return;
      }

      const maxVal = parseFloat(maximumValue);
      const minVal = parseFloat(minimumValue);
      if (isNaN(maxVal) || isNaN(minVal)) {
        setMaxValueError(undefined);
        return;
      }

      if (maxVal < minVal) {
        setMaxValueError(t('max-value-error', 'Max value must be greater than or equal to min value'));
        return;
      } else {
        setMaxValueError(undefined);
      }
    }

    applyFilters(minimumValue, maximumValue);
  }, [applyFilters, minimumValue, maximumValue, selectedComparisonValue.value, t]);

  const selectorActionTemplate = (props: DropDownActionTemplateProps) => {
    return <IconButton onClick={props.changeDropDownVisibility} icon="Filter" iconSize={OdinIconSize.ExtraSmall} />;
  };

  return (
    <>
      <OverlayPanel
        visible={visible}
        target={target}
        targetPosition={DomTargetPosition.BottomLeft}
        hidden={onHidePanel}
        scrollDetection={scrollDetection}
        className="odin-mt-2 odin-p-2 odin-bg-mono-1 odin-shadow-lg odin-border odin-border-gray-4 odin-rounded-md"
        shouldScrollCloseOverlay
        shouldRemainOnScreen
        shouldCheckZIndex
      >
        <>
          <DropDown
            data={comparisonDropDownData}
            textField="text"
            valueField="value"
            value={selectedComparisonValue}
            onChange={onComparisonValueChange}
            actionTemplate={selectorActionTemplate}
            dropDownStyles="odin-bg-primary-5 odin-border-primary-1 odin-rounded-full odin-text-primary-1 odin-px-2"
            optionSelectedStyle="odin-bg-primary-5"
            optionHoverStyle="hover:odin-bg-primary-5"
            allowSearch={false}
            allowClear={false}
          />
          <div className="odin-flex odin-flex-col odin-gap-3 odin-pt-3 odin-px-1">
            <Input
              name="min"
              type="number"
              value={minimumValue}
              placeholder={
                selectedComparisonValue.value === ContinuousScrollerComparisonType.Between
                  ? t('min-value', 'Min value')
                  : t('value', 'Value')
              }
              onChange={onMinimumValueChange}
            />
            {selectedComparisonValue.value === ContinuousScrollerComparisonType.Between && (
              <Input
                name="max"
                type="number"
                value={maximumValue}
                placeholder={t('max-value', 'Max value')}
                onChange={onMaximumValueChange}
                error={maxValueError}
              />
            )}
            <ClearFilterButton onClick={resetFilters} />
          </div>
        </>
      </OverlayPanel>
    </>
  );
}

export default DataGridNumericFilterPanel;
